import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from '@/api/rpc/client';
import type {
  UpdateRebateInvoiceRequest,
  UpdateRebateInvoiceResponse,
} from '@erp/rpc';
import { useToast } from 'src/context/ToastContext';

/**
 * Hook to update a rebate invoice's invoice number
 */
export function useUpdateRebateInvoice() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation<
    UpdateRebateInvoiceResponse,
    Error,
    UpdateRebateInvoiceRequest
  >({
    mutationFn: async (input) => {
      return await rpcClient.rebates.invoices.update(input);
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refresh the data
      // Invalidate all rebate invoice queries (matches useRebateInvoicesByAgreement query keys)
      // The query keys are structured as ['rebates/invoices/agreement/${agreementId}', queryParams]
      // so we need to match any query key where the first element starts with 'rebates/invoices'
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return (
            Array.isArray(key) &&
            key.length > 0 &&
            typeof key[0] === 'string' &&
            (key[0].startsWith('rebates/invoices') ||
              key[0].startsWith('rebates/supplier-reported-amounts'))
          );
        },
      });
      queryClient.invalidateQueries({
        queryKey: ['rebateAgreements'],
      });

      showToast({
        message: 'Invoice number updated successfully',
        color: 'success',
        icon: 'check',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update invoice number',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
